import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save, Mail, Info } from "lucide-react";
import { TIPOS_RESPUESTA, IDIOMAS } from "@/lib/constants";

const ORDEN_TIPOS = ["confirmacion_cancelacion", "confirmacion_cambio", "rechazo_fuera_plazo", "protocolo_gyg"];
const TOKENS = ["{{cliente}}", "{{tour}}", "{{fecha}}", "{{hora}}", "{{codigo}}", "{{horas_politica}}", "{{agencia}}"];

export default function PlantillasConfig() {
  const [plantillas, setPlantillas] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => setPlantillas(data.plantillas_email || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, []);

  const setCampo = (id, campo, valor) => {
    setPlantillas((ps) => ps.map((p) => (p.id === id ? { ...p, [campo]: valor } : p)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { plantillas_email: plantillas });
      toast.success("Plantillas guardadas.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!plantillas) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const porTipo = (tipo) => plantillas.filter((p) => p.tipo === tipo);

  return (
    <div className="space-y-4" data-testid="plantillas-config">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-2xl">
          Plantillas de respuesta automática al cliente (bilingües es/en). Se usan al confirmar/rechazar cambios
          y cancelaciones. El idioma se elige según el idioma del cliente.
        </p>
        <Button onClick={save} disabled={saving} data-testid="plantillas-save" className="gap-2 shrink-0">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar plantillas
        </Button>
      </div>

      <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-[12px] text-slate-600">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
        <div>
          Variables disponibles (se reemplazan automáticamente):{" "}
          {TOKENS.map((t) => (
            <code key={t} className="mx-0.5 rounded bg-white border border-slate-200 px-1 text-[11px] text-slate-700">{t}</code>
          ))}
        </div>
      </div>

      {ORDEN_TIPOS.map((tipo) => (
        <Card key={tipo} className="p-5 space-y-4" data-testid={`plantilla-tipo-${tipo}`}>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-700">
              <Mail className="h-4.5 w-4.5" />
            </div>
            <h3 className="font-heading font-semibold text-slate-900">{TIPOS_RESPUESTA[tipo] || tipo}</h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {porTipo(tipo).sort((a, b) => (a.idioma > b.idioma ? 1 : -1)).map((p) => (
              <div key={p.id} className="space-y-2 rounded-lg border border-slate-200 p-3">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-slate-100 text-slate-700 border-slate-200">
                  {IDIOMAS[p.idioma] || p.idioma?.toUpperCase()}
                </span>
                <div className="space-y-1.5">
                  <Label className="text-xs">Asunto</Label>
                  <Input
                    value={p.asunto || ""}
                    onChange={(e) => setCampo(p.id, "asunto", e.target.value)}
                    data-testid={`plantilla-asunto-${tipo}-${p.idioma}`}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Cuerpo</Label>
                  <Textarea
                    value={p.cuerpo || ""}
                    onChange={(e) => setCampo(p.id, "cuerpo", e.target.value)}
                    rows={8}
                    className="text-[13px] leading-relaxed"
                    data-testid={`plantilla-cuerpo-${tipo}-${p.idioma}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
